<script setup lang="ts">
import Draggable from 'vuedraggable';
import { ref, watch } from 'vue';
import { cloneDeep } from 'lodash';

import { AutoCompleteType } from '@apps/main/new-desktop/modules/autoComplete';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';

import { AppSvgIcon } from '@libs/ui';

import CarTransportSegment from './CarTransportSegment.vue';
import StandardContentDraggable from './StandardContentDraggable.vue';

interface EditTripSegmentsProps {
  segments: EditTripSegment[];
}

const props = defineProps<EditTripSegmentsProps>();

interface EditTripSegmentsEmits {
  (
    eventName: 'change-destination',
    payload: {
      segment: EditTripSegment;
      autocomplete:
        | AutoCompleteType.Destination
        | AutoCompleteType.StartingPosition
        | AutoCompleteType.EndingPosition;
    }
  ): void;
  (eventName: 'change-trip-order', segments: EditTripSegment[]): void;
  (eventName: 'change-number-of-nights', data: EditTripSegment): void;
  (eventName: 'remove-destination', segment: EditTripSegment): void;
  (eventName: 'close'): void;
}

const emit = defineEmits<EditTripSegmentsEmits>();
const draggableSegments = ref<EditTripSegment[]>([]);
const startingLocationSegment = ref<EditTripSegment>();
const endLocationSegment = ref<EditTripSegment>();
const draggableIndex = ref<number | undefined>(undefined);

function onEditTrip(event: { newIndex: number; oldIndex: number }): void {
  if (event.newIndex === event.oldIndex) {
    return;
  }

  resetDraggableIndex();
  emit('change-trip-order', draggableSegments.value);
}

function setDraggableIndex(event: { oldIndex: number }): void {
  draggableIndex.value = event.oldIndex;
}

function resetDraggableIndex(): void {
  draggableIndex.value = undefined;
}

function changeNumberOfNights(
  newNumberOfNights: number,
  segment: EditTripSegment
): void {
  emit('change-number-of-nights', {
    ...segment,
    numberOfNights: newNumberOfNights,
  });
}

function changeDestination(
  segment: EditTripSegment,
  autocomplete:
    | AutoCompleteType.Destination
    | AutoCompleteType.StartingPosition
    | AutoCompleteType.EndingPosition
): void {
  emit('change-destination', { segment, autocomplete });
}

watch(
  () => props.segments,
  () => {
    draggableSegments.value = cloneDeep(props.segments.slice(1, -1));
    startingLocationSegment.value = cloneDeep(props.segments[0]);
    endLocationSegment.value = cloneDeep(
      props.segments[props.segments.length - 1]
    );
  },
  { immediate: true }
);
</script>

<template>
  <div class="edit-trip-segments">
    <div v-if="startingLocationSegment" class="segment user-location-segment">
      <div class="details-wrapper">
        <div class="details">
          <StandardContentDraggable
            :destination-name="startingLocationSegment.destinationName"
            is-user-location
            @change-destination="
              changeDestination(
                startingLocationSegment,
                AutoCompleteType.StartingPosition
              )
            "
          />
        </div>
        <CarTransportSegment
          v-if="draggableSegments.length"
          :car-trip-info="startingLocationSegment.carTripInfo"
        />
      </div>
    </div>
    <Draggable
      v-model="draggableSegments"
      item-key="key"
      animation="200"
      handle=".drag-handle"
      drag-class="dragging-element"
      :force-fallback="true"
      :disabled="props.segments.length === 1"
      @start="setDraggableIndex"
      @unchoose="resetDraggableIndex"
      @update="onEditTrip"
    >
      <template #item="{ element, index }">
        <div class="segment">
          <div
            class="drag-handle"
            :class="{ draggable: draggableSegments.length > 1 }"
          >
            <AppSvgIcon
              name="drag"
              :color="
                index === draggableIndex
                  ? 'color-secondary-40'
                  : 'color-secondary-85'
              "
            />
          </div>
          <div class="details-wrapper">
            <div class="details">
              <StandardContentDraggable
                :destination-name="element.destinationName"
                :number-of-nights="element.numberOfNights"
                @change-destination="
                  changeDestination(element, AutoCompleteType.Destination)
                "
                @remove-destination="emit('remove-destination', element)"
                @change-number-of-nights="changeNumberOfNights($event, element)"
              />
            </div>
            <CarTransportSegment :car-trip-info="element.carTripInfo" />
          </div>
        </div>
      </template>
    </Draggable>
    <div
      v-if="draggableSegments.length && endLocationSegment"
      class="segment user-location-segment"
    >
      <div class="details-wrapper">
        <div class="details">
          <StandardContentDraggable
            :destination-name="endLocationSegment.destinationName"
            is-user-location
            @change-destination="
              changeDestination(
                endLocationSegment,
                AutoCompleteType.EndingPosition
              )
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.edit-trip-segments {
  --segment-height: 56px;

  display: flex;
  flex-direction: column;
  user-select: none;

  .segment {
    display: flex;
    align-items: flex-start;
    position: relative;

    .drag-handle {
      --drag-area-width: 36px;

      display: flex;
      align-items: center;

      height: 100%;
      min-width: var(--drag-area-width);
      padding-top: var(--spacing-16);

      &.draggable {
        cursor: pointer;
      }
    }
    .details-wrapper {
      width: 100%;
      .details {
        background-color: var(--color-white);
        display: flex;
        flex-grow: 1;
        flex-direction: column;
        border: 1px solid var(--color-secondary-93);
        border-radius: var(--border-radius-8);
        overflow: hidden;
      }
    }

    &.dragging-element {
      background: var(--color-white);
      opacity: 1 !important;
      &::before {
        display: none;
      }
    }

    &.user-location-segment {
      margin-left: 36px;
    }
  }
}
</style>
